(() => {
  if (window.customFreeGiftBannerInitialized) return;
  window.customFreeGiftBannerInitialized = true;

  const colorNames = {
    graphite: "#41403e",
    rust: "#9f8984",
    jade: "#acc5bb",
    sand: "#e3ddd6",
    grey: '#aeb1b4',
    gray: '#aeb1b4'
  };

  const initColorSwatches = (root = document) => {
    root.querySelectorAll('.custom-free-gift-color-swatch').forEach((swatch) => {
      if (swatch.style.getPropertyValue('--swatch')) return;
      const colorName = (swatch.dataset.colorValue || '').trim().toLowerCase();
      const normalized = colorName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const mappedColor = colorNames[colorName] || colorNames[normalized] ||
        Object.entries(colorNames).find(([name]) => normalized.includes(name))?.[1];
      const cssColor = mappedColor || (window.CSS?.supports('color', colorName) ? colorName : '');
      if (cssColor) swatch.style.setProperty('--swatch', cssColor);
    });
  };

  initColorSwatches();
  document.addEventListener('theme:cart:loaded', () => initColorSwatches());

  const initializedCarousels = new WeakSet();
  const initGiftCarousels = (root = document) => {
    root.querySelectorAll('.custom-free-gift-widget').forEach((carousel) => {
      if (initializedCarousels.has(carousel)) return;
      initializedCarousels.add(carousel);

      let startX = 0;
      let startScrollLeft = 0;
      let dragging = false;

      carousel.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button, a, input, select')) return;
        dragging = true;
        startX = event.clientX;
        startScrollLeft = carousel.scrollLeft;
        carousel.classList.add('is-dragging');
        carousel.setPointerCapture(event.pointerId);
      });

      carousel.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        carousel.scrollLeft = startScrollLeft - (event.clientX - startX);
      });

      const stopDragging = () => {
        dragging = false;
        carousel.classList.remove('is-dragging');
      };
      carousel.addEventListener('pointerup', stopDragging);
      carousel.addEventListener('pointercancel', stopDragging);
      carousel.addEventListener('lostpointercapture', stopDragging);
    });
  };

  initGiftCarousels();
  document.addEventListener('theme:cart:loaded', () => initGiftCarousels());

  let giftWidgetRefreshTimer;
  let giftWidgetRefreshInProgress = false;
  const refreshGiftWidget = async () => {
    if (giftWidgetRefreshInProgress) return;
    giftWidgetRefreshInProgress = true;

    try {
      const response = await fetch(`${window.Shopify.routes.root}?id=cart-drawer`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error('Unable to refresh gift offers.');

      const html = await response.text();
      const refreshedDocument = new DOMParser().parseFromString(html, 'text/html');
      const refreshedWidget = refreshedDocument.querySelector('#progress_bars_section .custom-free-gift-widget') ||
        refreshedDocument.querySelector('.custom-free-gift-widget');
      const currentWidgets = document.querySelectorAll('.custom-free-gift-widget');

      currentWidgets.forEach((currentWidget) => {
        if (refreshedWidget) {
          currentWidget.replaceWith(refreshedWidget.cloneNode(true));
        } else {
          currentWidget.remove();
        }
      });

      initColorSwatches();
      initGiftCarousels();
    } catch (error) {
      console.warn('Custom free gift offers could not be refreshed:', error);
    } finally {
      giftWidgetRefreshInProgress = false;
    }
  };

  const scheduleGiftWidgetRefresh = (delay = 300) => {
    clearTimeout(giftWidgetRefreshTimer);
    giftWidgetRefreshTimer = setTimeout(refreshGiftWidget, delay);
  };

  document.addEventListener('product:added', () => scheduleGiftWidgetRefresh());
  document.addEventListener('custom:free-gift-added', () => scheduleGiftWidgetRefresh());
  document.addEventListener('theme:cart:update', () => scheduleGiftWidgetRefresh(700));

  const setToggleIcon = (button, expanded) => {
    button.dataset.expanded = expanded ? 'true' : 'false';
    button.setAttribute('aria-expanded', String(expanded));
    button.setAttribute('aria-label', expanded ? 'Close gift options' : 'Choose gift options');
    button.classList.toggle('is-expanded', expanded);
  };

  const addGift = async (item, button, variantId) => {
    if (!variantId || button.disabled) return;
    button.disabled = true;

    try {
      const ruleId = item.dataset.ruleId;
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          properties: {
            _aiod_free_gift: 'true',
            _aiod_rule_id: ruleId,
            [`_aiod_gift_${ruleId}`]: 'true'
          }
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Unable to add gift.');

      item.hidden = true;
      document.dispatchEvent(new CustomEvent('custom:free-gift-added', {
        bubbles: true,
        detail: { item: data, ruleId }
      }));
      if (window.cart && typeof window.cart.getCart === 'function') window.cart.getCart();
    } catch (error) {
      console.error('Custom free gift:', error);
      button.disabled = false;
    }
  };

  document.addEventListener('click', (event) => {
    const control = event.target.closest('[data-gift-add], [data-gift-variant-option], [data-gift-submit]');
    if (!control) return;

    // The banner is outside #cart-drawer, so keep its controls from being
    // interpreted as clicks outside the drawer by the theme.
    event.stopPropagation();

    const item = control.closest('[data-gift-item]');
    if (!item) return;

    if (control.matches('[data-gift-variant-option]')) {
      const optionGroup = control.closest('.custom-free-gift-option-group');
      optionGroup.querySelectorAll('[data-gift-variant-option]').forEach((option) => {
        const selected = option === control;
        option.classList.toggle('is-selected', selected);
        option.setAttribute('aria-pressed', String(selected));
      });

      const variants = JSON.parse(item.dataset.variants || '[]');
      const groups = [...item.querySelectorAll('.custom-free-gift-option-group')];
      const selectedValues = groups.map((group) => group.querySelector('[data-gift-variant-option].is-selected')?.dataset.optionValue);
      const matchesSelection = (variant, positions = groups.map((_, index) => index)) => positions.every((index) => variant.options[index] === selectedValues[index]);
      let matchingVariant = variants.find((variant) => matchesSelection(variant));

      if (!matchingVariant) {
        const changedPosition = Number(control.dataset.optionPosition) - 1;
        matchingVariant = variants.find((variant) => matchesSelection(variant, [changedPosition]));
        if (matchingVariant) {
          groups.forEach((group, index) => {
            const selectedValue = matchingVariant.options[index];
            group.querySelectorAll('[data-gift-variant-option]').forEach((option) => {
              const selected = option.dataset.optionValue === selectedValue;
              option.classList.toggle('is-selected', selected);
              option.setAttribute('aria-pressed', String(selected));
            });
          });
        }
      }

      if (matchingVariant) item.dataset.selectedVariantId = matchingVariant.id;
      return;
    }

    if (control.matches('[data-gift-submit]')) {
      addGift(item, control, item.dataset.selectedVariantId || item.dataset.variantId);
      return;
    }

    const panel = item.querySelector('[data-gift-variant-panel]');
    if (panel) {
      const expanded = control.dataset.expanded !== 'true';
      panel.hidden = !expanded;
      item.classList.toggle('is-expanded', expanded);
      setToggleIcon(control, expanded);
      if (expanded) panel.querySelector('[data-gift-variant-option]')?.focus();
      return;
    }

    addGift(item, control, item.dataset.variantId);
  }, true);
})();
